import { typography, ItemsList, Divider, Flex } from '@retailer-platform/shared-components/src/tds'
import styled from '@emotion/styled'
import React from 'react'
import { type NavMenuHierarchyL1, type NavMenuHierarchyL2 } from '../nav-v2/NavMenuHierarchy'
import { useDashIntl, useDashMessages } from '../../../intl/intl.hooks'
import { ToolPermissionRow } from './ToolPermissionRow'

const ColumnHeader = styled.div({
  ...typography.bodyEmphasized,
})

type ToolPermissionSelectorProps = {
  item: NavMenuHierarchyL1
  selectedPermissionSlugs: Set<string>
  handlePermissionSlugsChange: (newSelectedPermissionSlugs: Set<string>) => void
  readOnly?: boolean
  roleAssignedPermissionSlugs?: Set<string>
  userRoles?: { label: string; permissions: { slug: string }[] }[]
}

const L1Text = styled.div(({ theme }) => ({
  // ...typography.bodyEmphasized,
  color: theme.colors.systemGrayscale60,
  ...typography.bodyEmphasized,
}))

export const ToolPermissionSelectorL1Section = ({
  item,
  selectedPermissionSlugs,
  handlePermissionSlugsChange,
  readOnly,
  roleAssignedPermissionSlugs,
  userRoles,
}: ToolPermissionSelectorProps) => {
  const dashIntl = useDashIntl()

  const i18n = useDashMessages({
    permission: 'dashboard.permissions.column.permission',
    description: 'dashboard.permissions.column.description',
    accessControl: 'dashboard.permissions.column.accessControl',
    restrictions: 'dashboard.permissions.column.restrictions',
    associatedRoles: 'dashboard.permissions.column.associatedRoles',
  })

  const createToolPermissionRow = (tool: NavMenuHierarchyL2, showDivider: boolean) => (
    <>
      <ToolPermissionRow
        key={tool.name}
        tool={tool}
        selectedPermissionSlugs={selectedPermissionSlugs}
        handlePermissionSlugsChange={handlePermissionSlugsChange}
        readOnly={readOnly}
        roleAssignedPermissionSlugs={roleAssignedPermissionSlugs}
        userRoles={userRoles}
      />
      {showDivider && <Divider />}
    </>
  )

  const handleClick = (event: React.MouseEvent) => {
    // allows clicks inside the accordion without closing it
    event.stopPropagation()
  }

  const numberOfColumns = userRoles ? 4 : 3
  const rowWidth = (100 / numberOfColumns).toFixed(2) + '%'

  return (
    <div css={{ width: '860px', marginTop: '24px', marginLeft: '-8px' }} onClick={handleClick}>
      <ItemsList.Section>
        <Flex.Row gap="24px" alignItems="center">
          <Flex.Column css={{ width: rowWidth }}>
            <ColumnHeader>{i18n.permission}</ColumnHeader>
          </Flex.Column>
          <Flex.Column css={{ width: rowWidth }}>
            <ColumnHeader>{i18n.description}</ColumnHeader>
          </Flex.Column>
          <Flex.Column css={{ width: rowWidth }}>
            <ColumnHeader>{readOnly ? i18n.restrictions : i18n.accessControl}</ColumnHeader>
          </Flex.Column>
          {userRoles && (
            <Flex.Column css={{ width: rowWidth }}>
              <ColumnHeader>{i18n.associatedRoles}</ColumnHeader>
            </Flex.Column>
          )}
        </Flex.Row>
      </ItemsList.Section>
      <Divider />

      {item.hasL2Children &&
        item.children.map(l2Section => (
          <React.Fragment key={l2Section.name}>
            <L1Text css={{ marginTop: '0px', marginBottom: '16px' }}>
              {dashIntl.formatMessage({ id: l2Section.name as any })}
            </L1Text>
            {l2Section.children?.map(tool => createToolPermissionRow(tool, true))}
          </React.Fragment>
        ))}

      {!item.hasL2Children &&
        item.children.map((tool, index) =>
          createToolPermissionRow(tool, index < item.children.length - 1)
        )}
    </div>
  )
}
